import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useQuotes } from '../context/QuotesContext';
import { clearActiveContactForConfigurator } from '../lib/contactConfigurator';
import ConfiguratorNavActions from '../components/ConfiguratorNavActions';
import { 
  LogOut, Users, FileText, UserPlus, Plus, Search, 
  Mail, Phone, MapPin, Calendar, DollarSign,
  Menu, X, LayoutDashboard, ChevronLeft, ChevronRight,
  TrendingUp, Activity, Briefcase, ArrowRight, Eye, Loader2, Trash2, Settings, Filter,
  Clock, CheckCircle2, Send, FileEdit, AlertTriangle, StickyNote, Ban, ClipboardList, Printer, Copy
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import QuoteView from '../components/QuoteView';
import OpdrachtbonEditor, {
  prefillOpdrachtbonFromQuote,
} from '../components/OpdrachtbonEditor';
import { printOpdrachtbon } from '../lib/opdrachtbonPrint';
import axios from 'axios';
import { API_URL } from '../config/api';
import { APP_LOGO_SRC } from '../config/branding';
import { useTranslation } from '../context/TranslationContext';
import {
  getQuoteCreatorInfo,
  quoteBelongsToTeamMember,
  countQuotesForTeamMember,
  findTeamMemberById,
} from '../lib/quoteSession';
import { formatQuoteClientDisplayName } from '../lib/contactConfigurator';
import {
  readStoredQuotePage,
  storeQuotePage,
  clearStoredQuotePage,
  resolveQuoteListPage,
  resolveQuoteListSearch,
  resolveQuoteListStatus,
  buildQuotesDashboardReturnPath,
  storeQuoteSearch,
  storeQuoteStatus,
} from '../lib/quoteListPagination';

const isAdminUser = (u) => String(u?.role || '').toLowerCase() === 'admin';

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

/** Display/filter status: past valid_until (sent/open offers, not draft/accepted/lost) counts as expired. */
const getQuoteDisplayStatus = (quote) => {
  const raw = String(quote?.status || 'draft').toLowerCase();
  if (raw === 'accepted' || raw === 'rejected' || raw === 'draft' || raw === 'lost') return raw;
  if (quote?.valid_until) {
    const until = new Date(quote.valid_until);
    until.setHours(0, 0, 0, 0);
    if (until < startOfToday()) return 'expired';
  }
  return raw;
};

const getValidityMeta = (quote) => {
  if (!quote?.valid_until) {
    return { kind: 'none', daysLeft: null, isExpired: false };
  }
  const until = new Date(quote.valid_until);
  until.setHours(0, 0, 0, 0);
  const today = startOfToday();
  const daysLeft = Math.round((until - today) / (1000 * 60 * 60 * 24));
  if (daysLeft < 0) {
    return { kind: 'expired', daysLeft, isExpired: true };
  }
  return { kind: 'open', daysLeft, isExpired: false };
};

/** Compact page list: 1 … 4 5 6 … 213 */
function getCompactPaginationPages(currentPage, totalPages) {
  if (totalPages <= 1) return [1];
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages = new Set([
    1,
    totalPages,
    currentPage,
    currentPage - 1,
    currentPage + 1,
  ]);

  const sorted = [...pages]
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((a, b) => a - b);

  const result = [];
  let previous = 0;
  for (const page of sorted) {
    if (previous && page - previous > 1) {
      result.push('ellipsis');
    }
    result.push(page);
    previous = page;
  }
  return result;
}

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useTranslation();
  const { user, logout, isAuthenticated, loading: authLoading } = useAuth();
  const { activeBackendQuote, clearBackendQuoteSession, startNewQuoteSession } = useQuotes();
  const [activeTab, setActiveTab] = useState('overview');
  const [contacts, setContacts] = useState([]);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [teamMembers, setTeamMembers] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [teamLoading, setTeamLoading] = useState(true);
  const [quotesLoading, setQuotesLoading] = useState(true);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedContactSearch, setDebouncedContactSearch] = useState('');
  const [quoteSearchTerm, setQuoteSearchTerm] = useState(() =>
    resolveQuoteListSearch(typeof window !== 'undefined' ? window.location : { search: '' }),
  );
  const [quoteCreatorFilter, setQuoteCreatorFilter] = useState('all');
  const [quoteStatusFilter, setQuoteStatusFilter] = useState(() =>
    resolveQuoteListStatus(typeof window !== 'undefined' ? window.location : { search: '' }),
  );
  const [quoteCurrentPage, setQuoteCurrentPage] = useState(() => readStoredQuotePage());
  const [contactCurrentPage, setContactCurrentPage] = useState(1);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [quoteViewOpen, setQuoteViewOpen] = useState(false);
  const [teamMemberDialogOpen, setTeamMemberDialogOpen] = useState(false);
  const [newTeamMember, setNewTeamMember] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'user',
    phone: ''
  });
  const [creatingMember, setCreatingMember] = useState(false);
  const [syncingGHL, setSyncingGHL] = useState(false);
  const [ghlSyncMessage, setGhlSyncMessage] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactDetailsOpen, setContactDetailsOpen] = useState(false);
  const [deleteMemberDialogOpen, setDeleteMemberDialogOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [deletingMember, setDeletingMember] = useState(false);
  const [deleteQuoteDialogOpen, setDeleteQuoteDialogOpen] = useState(false);
  const [quoteToDelete, setQuoteToDelete] = useState(null);
  const [deletingQuote, setDeletingQuote] = useState(false);
  const [duplicateQuoteDialogOpen, setDuplicateQuoteDialogOpen] = useState(false);
  const [quoteToDuplicate, setQuoteToDuplicate] = useState(null);
  const [duplicatingQuote, setDuplicatingQuote] = useState(false);
  const [lostQuoteDialogOpen, setLostQuoteDialogOpen] = useState(false);
  const [quoteToMarkLost, setQuoteToMarkLost] = useState(null);
  const [markingQuoteLost, setMarkingQuoteLost] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [notesQuote, setNotesQuote] = useState(null);
  const [notesText, setNotesText] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesError, setNotesError] = useState('');
  const [notesSavedFlash, setNotesSavedFlash] = useState(false);
  const [opdrachtbonDialogOpen, setOpdrachtbonDialogOpen] = useState(false);
  const [opdrachtbonQuote, setOpdrachtbonQuote] = useState(null);
  const [opdrachtbonData, setOpdrachtbonData] = useState(null);
  const [savingOpdrachtbon, setSavingOpdrachtbon] = useState(false);
  const [opdrachtbonError, setOpdrachtbonError] = useState('');
  const [opdrachtbonSavedFlash, setOpdrachtbonSavedFlash] = useState(false);
  const [pricingSettings, setPricingSettings] = useState({
    discount_enabled: false,
    discount_percentage: 0,
    margin_enabled: false,
    margin_percentage: 35,
    deduction_margin_enabled: false,
    deduction_margin_percentage: 0,
    surcharge_enabled: false,
    surcharge_amount: 0,
    workers_fixing_enabled: false,
    workers_fixing_amount: 1000,
    material_cost_enabled: false,
    material_cost_amount: 150,
    signed_quote_notification_emails: ['jurgen@vmkkozijnen.nl', 'nick@vmkkozijnen.nl'],
  });
  const [newNotificationEmail, setNewNotificationEmail] = useState('');
  const [savingPricingSettings, setSavingPricingSettings] = useState(false);
  
  const quotesPerPage = 5;
  const contactsPerPage = 50;

  // Helper function to get initials
  const getInitials = (firstName, lastName) => {
    const first = firstName ? firstName.charAt(0).toUpperCase() : '';
    const last = lastName ? lastName.charAt(0).toUpperCase() : '';
    return first + last || '?';
  };

  const fetchContactsPage = useCallback(async () => {
    setContactsLoading(true);
    try {
      const offset = (Math.max(1, contactCurrentPage) - 1) * contactsPerPage;
      const params = { limit: contactsPerPage, offset };
      const trimmed = (debouncedContactSearch || '').trim();
      if (trimmed) params.q = trimmed;
      const response = await axios.get(`${API_URL}/api/contacts`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        params,
        timeout: 30000,
      });
      setContacts(response.data || []);
      const total = parseInt(response.headers?.['x-total-count'], 10);
      setContactsTotal(Number.isFinite(total) ? total : (response.data || []).length);
    } finally {
      setContactsLoading(false);
    }
  }, [contactCurrentPage, debouncedContactSearch]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedContactSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (authLoading || !isAuthenticated) return;
    fetchContactsPage().catch((error) => console.error('Error fetching contacts:', error));
  }, [authLoading, isAuthenticated, fetchContactsPage]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    fetchData();
  }, [isAuthenticated, authLoading, navigate, user?.id, user?.role]);

  // Sync tab / creator / page FROM the URL only. Do not depend on activeTab —
  // otherwise a sidebar click that only sets state gets overwritten by ?tab=…
  // still sitting in the URL (menu looked "stuck" on Quotes).
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tab = urlParams.get('tab');
    const creator = urlParams.get('creator');
    const allowedTabs = ['overview', 'contacts', 'team', 'quotes'];
    if (isAdminUser(user)) allowedTabs.push('pricing-settings');
    if (tab && allowedTabs.includes(tab)) {
      setActiveTab(tab);
    } else if (tab === 'pricing-settings') {
      setActiveTab('overview');
    } else if (!tab) {
      setActiveTab('overview');
    }
    if (isAdminUser(user) && creator) {
      setQuoteCreatorFilter(creator);
    } else if (tab === 'quotes' && !creator) {
      setQuoteCreatorFilter('all');
    }
    if (tab === 'quotes') {
      const page = resolveQuoteListPage(location);
      setQuoteCurrentPage(page);
      if (page > 1) {
        storeQuotePage(page);
      }
      const search = resolveQuoteListSearch(location);
      setQuoteSearchTerm(search);
      storeQuoteSearch(search);
      const status = resolveQuoteListStatus(location);
      setQuoteStatusFilter(status);
      storeQuoteStatus(status);
    }
  }, [location.search, location.state?.quotesPage, location.state?.quoteSearch, location.state?.quoteStatus, user]);

  const goToTab = (tabId) => {
    setActiveTab(tabId);
    const params = new URLSearchParams(location.search);
    if (tabId === 'overview') {
      params.delete('tab');
    } else {
      params.set('tab', tabId);
    }
    // Creator filter only applies on Quotes
    if (tabId !== 'quotes') {
      params.delete('creator');
      params.delete('page');
      params.delete('q');
      params.delete('status');
      setQuoteCreatorFilter('all');
    }
    const qs = params.toString();
    navigate(qs ? `/dashboard?${qs}` : '/dashboard', { replace: true });
  };

  const quoteCreatorOptions = useMemo(() => {
    if (!isAdminUser(user)) return [];
    return teamMembers
      .map((member) => ({
        id: member.id,
        name: `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email,
        email: member.email,
        quoteCount: countQuotesForTeamMember(quotes, member, teamMembers),
      }))
      .sort((a, b) => a.name.localeCompare(b.name, 'nl'));
  }, [teamMembers, quotes, user]);

  const viewMemberQuotes = (memberId) => {
    const id = String(memberId);
    setQuoteCreatorFilter(id);
    setQuoteCurrentPage(1);
    clearStoredQuotePage();
    setActiveTab('quotes');
    navigate(`/dashboard?tab=quotes&creator=${id}`);
  };

  const handleQuoteCreatorFilterChange = (value) => {
    setQuoteCreatorFilter(value);
    setQuoteCurrentPage(1);
    clearStoredQuotePage();
    const params = new URLSearchParams(location.search);
    params.set('tab', 'quotes');
    params.delete('page');
    if (value === 'all') {
      params.delete('creator');
    } else {
      params.set('creator', value);
    }
    navigate(`/dashboard?${params.toString()}`, { replace: true });
  };

  const fetchData = async () => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
    const requestConfig = { headers, timeout: 60000 };
    const admin = isAdminUser(user);
    // Contacts load via fetchContactsPage effect (search/pagination)

    setTeamLoading(true);
    axios
      .get(`${API_URL}/api/team`, requestConfig)
      .then((response) => setTeamMembers(response.data))
      .catch((error) => console.error('Error fetching team:', error))
      .finally(() => setTeamLoading(false));

    setQuotesLoading(true);
    axios
      .get(`${API_URL}/api/quotes`, requestConfig)
      .then((response) => setQuotes(response.data))
      .catch((error) => console.error('Error fetching quotes:', error))
      .finally(() => setQuotesLoading(false));

    if (admin) {
      axios
        .get(`${API_URL}/api/pricing-settings`, requestConfig)
        .then((response) => {
          if (response?.data) {
            const emails = Array.isArray(response.data.signed_quote_notification_emails)
              ? response.data.signed_quote_notification_emails
              : ['jurgen@vmkkozijnen.nl', 'nick@vmkkozijnen.nl'];
            setPricingSettings({
              ...response.data,
              signed_quote_notification_emails: emails,
            });
          }
        })
        .catch(() => {});
    }
  };

  const handleSavePricingSettings = async () => {
    if (!isAdminUser(user)) return;
    try {
      setSavingPricingSettings(true);
      
      // Validate and correct margin minimum; discount is set per quote, not in admin
      let settingsToSave = { ...pricingSettings };
      settingsToSave.discount_enabled = false;
      settingsToSave.discount_percentage = 0;
      settingsToSave.signed_quote_notification_emails = (
        Array.isArray(settingsToSave.signed_quote_notification_emails)
          ? settingsToSave.signed_quote_notification_emails
          : []
      )
        .map((email) => String(email || '').trim())
        .filter((email) => email && email.includes('@'));
      if (settingsToSave.signed_quote_notification_emails.length === 0) {
        settingsToSave.signed_quote_notification_emails = [
          'jurgen@vmkkozijnen.nl',
          'nick@vmkkozijnen.nl',
        ];
      }
      if (settingsToSave.margin_enabled) {
        const marginValue = parseFloat(settingsToSave.margin_percentage);
        if (isNaN(marginValue) || marginValue < 35) {
          settingsToSave.margin_percentage = 35;
          setPricingSettings(settingsToSave);
        }
      }
      if (settingsToSave.deduction_margin_enabled) {
        const deductionValue = parseFloat(settingsToSave.deduction_margin_percentage);
        if (isNaN(deductionValue) || deductionValue < 0) {
          settingsToSave.deduction_margin_percentage = 0;
        } else if (deductionValue > 100) {
          settingsToSave.deduction_margin_percentage = 100;
        }
      }
      
      const response = await axios.put(
        `${API_URL}/api/pricing-settings`,
        settingsToSave,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
        }
      );
      let finalSettings = response.data;
      if (isAdminUser(user)) {
        try {
          const adminResponse = await axios.put(
            `${API_URL}/api/admin/pricing-settings`,
            {
              workers_fixing_enabled: Boolean(pricingSettings.workers_fixing_enabled),
              workers_fixing_amount: Number(pricingSettings.workers_fixing_amount) || 1000,
              material_cost_enabled: Boolean(pricingSettings.material_cost_enabled),
              material_cost_amount: Number(pricingSettings.material_cost_amount) || 150
            },
            { headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` } }
          );
          finalSettings = { ...response.data, ...adminResponse.data };
        } catch (adminErr) {
          console.error('Error saving admin pricing settings:', adminErr);
        }
      }
      setPricingSettings(finalSettings);
      alert('Pricing settings saved successfully!');
    } catch (error) {
      console.error('Error saving pricing settings:', error);
      const errorMessage = error.response?.data?.detail || 'Error saving pricing settings.';
      alert(errorMessage);
    } finally {
      setSavingPricingSettings(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleDeleteMember = async () => {
    if (!memberToDelete) return;
    
    try {
      setDeletingMember(true);
      await axios.delete(`${API_URL}/api/team/${memberToDelete.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      // Refresh team members list
      const teamRes = await axios.get(`${API_URL}/api/team`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      setTeamMembers(teamRes.data);
      
      setDeleteMemberDialogOpen(false);
      setMemberToDelete(null);
    } catch (error) {
      console.error('Error deleting team member:', error);
      alert(error.response?.data?.detail || 'Failed to delete team member');
    } finally {
      setDeletingMember(false);
    }
  };

  const openDeleteDialog = (member) => {
    setMemberToDelete(member);
    setDeleteMemberDialogOpen(true);
  };

  const openDeleteQuoteDialog = (quote) => {
    setQuoteToDelete(quote);
    setDeleteQuoteDialogOpen(true);
  };

  const openDuplicateQuoteDialog = (quote) => {
    setQuoteToDuplicate(quote);
    setDuplicateQuoteDialogOpen(true);
  };

  const openLostQuoteDialog = (quote) => {
    setQuoteToMarkLost(quote);
    setLostQuoteDialogOpen(true);
  };

  const handleMarkQuoteLost = async () => {
    if (!quoteToMarkLost?.id) return;
    try {
      setMarkingQuoteLost(true);
      const response = await axios.patch(
        `${API_URL}/api/quotes/${quoteToMarkLost.id}/status`,
        { status: 'lost' },
        { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } },
      );
      const updated = response.data;
      setQuotes((prev) =>
        prev.map((q) => (q.id === updated.id ? { ...q, ...updated } : q)),
      );
      setLostQuoteDialogOpen(false);
      setQuoteToMarkLost(null);
    } catch (error) {
      console.error('Error marking quote as lost:', error);
      alert(error.response?.data?.detail || t('dashboard.markLostError'));
    } finally {
      setMarkingQuoteLost(false);
    }
  };

  const openNotesDialog = (quote) => {
    setNotesQuote(quote);
    setNotesText(String(quote?.quote_data?.staffNotes || ''));
    setNotesError('');
    setNotesSavedFlash(false);
    setNotesDialogOpen(true);
  };

  const getOpdrachtbonFilledByName = (quote) => {
    const creator = getQuoteCreatorInfo(quote, teamMembers);
    if (creator?.name) return creator.name;
    const fromUser =
      `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.email || '';
    return fromUser;
  };

  const openOpdrachtbonDialog = async (quote) => {
    setOpdrachtbonQuote(quote);
    setOpdrachtbonError('');
    setOpdrachtbonSavedFlash(false);
    setOpdrachtbonDialogOpen(true);
    // Offertes list is slim (no drawings) — client fields first, then full quote
    setOpdrachtbonData(
      prefillOpdrachtbonFromQuote(quote, null, {
        filledBy: getOpdrachtbonFilledByName(quote),
      }),
    );

    let fullQuote = quote;
    try {
      const response = await axios.get(`${API_URL}/api/quotes/${quote.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        timeout: 60000,
      });
      fullQuote = response.data;
      setOpdrachtbonQuote(fullQuote);
    } catch (error) {
      console.warn('Could not load full quote for opdrachtbon', error);
    }

    const clientDetails =
      fullQuote?.quote_data?.clientDetails || quote?.quote_data?.clientDetails || {};
    const contactId =
      fullQuote?.contact_id ||
      quote?.contact_id ||
      clientDetails.contactId ||
      clientDetails.id ||
      null;

    let resolvedContact =
      (contactId && contacts.find((c) => Number(c.id) === Number(contactId))) ||
      null;

    if (!resolvedContact) {
      const targetName = String(
        formatQuoteClientDisplayName(clientDetails) || clientDetails.name || '',
      )
        .trim()
        .toLowerCase();
      if (targetName) {
        resolvedContact =
          contacts.find((c) => {
            const full = `${c.first_name || ''} ${c.last_name || ''}`.trim().toLowerCase();
            return full === targetName;
          }) || null;
      }
    }

    if (!resolvedContact && contactId) {
      try {
        const response = await axios.get(`${API_URL}/api/contacts/${contactId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        });
        resolvedContact = response.data;
      } catch (error) {
        console.warn('Could not load contact for opdrachtbon', error);
      }
    } else if (!resolvedContact) {
      const targetName = String(
        formatQuoteClientDisplayName(clientDetails) || clientDetails.name || '',
      ).trim();
      if (targetName) {
        try {
          const response = await axios.get(`${API_URL}/api/contacts`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
            params: { q: targetName, limit: 20, offset: 0 },
            timeout: 15000,
          });
          const rows = response.data || [];
          const needle = targetName.toLowerCase();
          resolvedContact =
            rows.find((c) => {
              const full = `${c.first_name || ''} ${c.last_name || ''}`.trim().toLowerCase();
              return full === needle;
            }) ||
            rows[0] ||
            null;
        } catch (error) {
          console.warn('Could not search contact for opdrachtbon', error);
        }
      }
    }

    setOpdrachtbonData(
      prefillOpdrachtbonFromQuote(fullQuote, resolvedContact, {
        filledBy: getOpdrachtbonFilledByName(fullQuote),
      }),
    );
  };

  const handlePrintOpdrachtbon = () => {
    if (!opdrachtbonData) return;
    setOpdrachtbonError('');
    printOpdrachtbon(opdrachtbonData, {
      quoteNumber: opdrachtbonQuote?.quote_number || '',
    });
  };

  const handleSaveOpdrachtbon = async () => {
    if (!opdrachtbonQuote?.id) return;
    try {
      setSavingOpdrachtbon(true);
      setOpdrachtbonError('');
      setOpdrachtbonSavedFlash(false);
      const response = await axios.patch(
        `${API_URL}/api/quotes/${opdrachtbonQuote.id}/opdrachtbon`,
        { opdrachtbon: opdrachtbonData || {} },
        { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } },
      );
      const updated = response.data;
      // Keep list rows slim (no drawings); keep dialog drawings for re-prefill
      setQuotes((prev) =>
        prev.map((q) => {
          if (q.id !== updated.id) return q;
          return {
            ...q,
            updated_at: updated.updated_at,
            quote_data: {
              ...(q.quote_data || {}),
              opdrachtbon: updated.quote_data?.opdrachtbon,
            },
          };
        }),
      );
      const mergedQuote = {
        ...updated,
        quote_data: {
          ...(opdrachtbonQuote.quote_data || {}),
          ...(updated.quote_data || {}),
          opdrachtbon: updated.quote_data?.opdrachtbon,
        },
      };
      setOpdrachtbonQuote(mergedQuote);
      setOpdrachtbonData(
        prefillOpdrachtbonFromQuote(mergedQuote, null, {
          filledBy: getOpdrachtbonFilledByName(mergedQuote),
        }),
      );
      setOpdrachtbonSavedFlash(true);
    } catch (error) {
      console.error('Error saving opdrachtbon:', error);
      setOpdrachtbonError(t('dashboard.opdrachtbonSaveError'));
    } finally {
      setSavingOpdrachtbon(false);
    }
  };

  const handleSaveQuoteNotes = async () => {
    if (!notesQuote?.id) return;
    try {
      setSavingNotes(true);
      setNotesError('');
      setNotesSavedFlash(false);
      const response = await axios.patch(
        `${API_URL}/api/quotes/${notesQuote.id}/notes`,
        { notes: notesText },
        { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } },
      );
      const updated = response.data;
      setQuotes((prev) =>
        prev.map((q) => (q.id === updated.id ? { ...q, ...updated } : q)),
      );
      setNotesQuote(updated);
      setNotesText(String(updated?.quote_data?.staffNotes || ''));
      setNotesSavedFlash(true);
    } catch (error) {
      console.error('Error saving quote notes:', error);
      setNotesError(t('dashboard.notesSaveError'));
    } finally {
      setSavingNotes(false);
    }
  };

  const handleDeleteQuote = async () => {
    if (!quoteToDelete) return;

    try {
      setDeletingQuote(true);
      await axios.delete(`${API_URL}/api/quotes/${quoteToDelete.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      });

      setQuotes((prev) => prev.filter((q) => q.id !== quoteToDelete.id));
      if (String(activeBackendQuote?.id) === String(quoteToDelete.id)) {
        clearBackendQuoteSession();
      }

      setDeleteQuoteDialogOpen(false);
      setQuoteToDelete(null);
    } catch (error) {
      console.error('Error deleting quote:', error);
      alert(error.response?.data?.detail || 'Failed to delete quote');
    } finally {
      setDeletingQuote(false);
    }
  };

  const handleDuplicateQuote = async () => {
    if (!quoteToDuplicate?.id || duplicatingQuote) return;
    try {
      setDuplicatingQuote(true);
      const response = await axios.post(
        `${API_URL}/api/quotes/${quoteToDuplicate.id}/duplicate`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } },
      );
      const duplicated = response.data;
      if (!duplicated?.id) {
        throw new Error('Duplicate response missing id');
      }
      setQuotes((prev) => [duplicated, ...prev]);
      setDuplicateQuoteDialogOpen(false);
      setQuoteToDuplicate(null);
      const quotesPage = getCurrentQuotesPage();
      navigate(`/dashboard/quotes/${duplicated.id}`, {
        state: {
          quote: duplicated,
          returnPath: buildQuotesDashboardReturnPath(location.search, quotesPage, {
            q: quoteSearchTerm,
            status: quoteStatusFilter,
          }),
          quotesPage,
          quoteSearch: quoteSearchTerm,
          quoteStatus: quoteStatusFilter,
        },
      });
    } catch (error) {
      console.error('Error duplicating quote:', error);
      const detail = error.response?.data?.detail;
      alert(
        typeof detail === 'string'
          ? detail
          : t('dashboard.duplicateQuoteError'),
      );
    } finally {
      setDuplicatingQuote(false);
    }
  };

  // Contacts are filtered + paginated server-side
  const totalContactPages = Math.max(1, Math.ceil(contactsTotal / contactsPerPage) || 1);
  const paginatedContacts = contacts;
  const contactStartIndex = (contactCurrentPage - 1) * contactsPerPage;
  const contactEndIndex = contactStartIndex + contacts.length;

  // Reset to page 1 when search term changes
  useEffect(() => {
    setContactCurrentPage(1);
  }, [debouncedContactSearch]);

  const quoteMatchesSearchAndCreator = useCallback((quote) => {
    if (isAdminUser(user) && quoteCreatorFilter !== 'all') {
      const member = findTeamMemberById(teamMembers, quoteCreatorFilter);
      if (!quoteBelongsToTeamMember(quote, member || quoteCreatorFilter, teamMembers)) {
        return false;
      }
    }
    if (!quoteSearchTerm) return true;
    const search = quoteSearchTerm.toLowerCase();
    const clientDetails = quote.quote_data?.clientDetails || {};
    const clientName =
      formatQuoteClientDisplayName(clientDetails) || clientDetails.name || '';
    const clientEmail = clientDetails.email || '';
    const quoteNumber = quote.quote_number || '';
    const creator = getQuoteCreatorInfo(quote, teamMembers);
    const endClient = clientDetails.endClientName || '';

    return (
      clientName.toLowerCase().includes(search) ||
      endClient.toLowerCase().includes(search) ||
      ('duurzaamadviesloket'.includes(search) && clientDetails.partnerType === 'duurzaamadviesloket') ||
      clientEmail.toLowerCase().includes(search) ||
      quoteNumber.toLowerCase().includes(search) ||
      (creator?.name || '').toLowerCase().includes(search) ||
      (creator?.email || '').toLowerCase().includes(search)
    );
  }, [quoteCreatorFilter, quoteSearchTerm, teamMembers, user]);

  const filteredQuotes = useMemo(() => {
    return quotes.filter((quote) => {
      if (!quoteMatchesSearchAndCreator(quote)) return false;
      if (quoteStatusFilter !== 'all' && getQuoteDisplayStatus(quote) !== quoteStatusFilter) {
        return false;
      }
      return true;
    });
  }, [quotes, quoteMatchesSearchAndCreator, quoteStatusFilter]);

  const quoteStatusCounts = useMemo(() => {
    const base = quotes.filter((quote) => quoteMatchesSearchAndCreator(quote));
    const counts = { all: base.length, draft: 0, sent: 0, accepted: 0, expired: 0, rejected: 0, lost: 0 };
    base.forEach((quote) => {
      const status = getQuoteDisplayStatus(quote);
      if (counts[status] !== undefined) counts[status] += 1;
    });
    return counts;
  }, [quotes, quoteMatchesSearchAndCreator]);

  const selectedCreatorLabel = quoteCreatorOptions.find(
    (m) => String(m.id) === String(quoteCreatorFilter)
  )?.name;

  const totalQuotePages = Math.max(1, Math.ceil(filteredQuotes.length / quotesPerPage) || 1);

  const updateQuotePage = useCallback(
    (page) => {
      const nextPage = Math.min(Math.max(1, page), totalQuotePages);
      setQuoteCurrentPage(nextPage);
      storeQuotePage(nextPage);
    },
    [totalQuotePages],
  );

  const getCurrentQuotesPage = useCallback(
    () => Math.max(quoteCurrentPage, readStoredQuotePage()),
    [quoteCurrentPage],
  );

  const quoteSearchInitialized = useRef(false);
  useEffect(() => {
    if (!quoteSearchInitialized.current) {
      quoteSearchInitialized.current = true;
      return;
    }
    setQuoteCurrentPage(1);
    clearStoredQuotePage();
  }, [quoteSearchTerm]);

  useEffect(() => {
    if (quotesLoading) return;
    if (quoteCurrentPage > totalQuotePages) {
      const clampedPage = totalQuotePages;
      setQuoteCurrentPage(clampedPage);
      storeQuotePage(clampedPage);
    }
  }, [quoteCurrentPage, totalQuotePages, quotesLoading]);

  const startIndex = (quoteCurrentPage - 1) * quotesPerPage;
  const endIndex = startIndex + quotesPerPage;
  const paginatedQuotes = filteredQuotes.length > quotesPerPage 
    ? filteredQuotes.slice(startIndex, endIndex)
    : filteredQuotes;

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-slate-100 text-slate-700 border-slate-200',
      sent: 'bg-sky-50 text-sky-800 border-sky-200',
      accepted: 'bg-emerald-50 text-emerald-800 border-emerald-200',
      rejected: 'bg-rose-50 text-rose-800 border-rose-200',
      expired: 'bg-amber-50 text-amber-900 border-amber-300',
      lost: 'bg-stone-100 text-stone-700 border-stone-300',
    };
    return colors[status] || colors.draft;
  };

  const getStatusAccent = (status) => {
    const accents = {
      draft: 'border-l-slate-400',
      sent: 'border-l-sky-500',
      accepted: 'border-l-emerald-500',
      rejected: 'border-l-rose-500',
      expired: 'border-l-amber-500',
      lost: 'border-l-stone-500',
    };
    return accents[status] || accents.draft;
  };

  const getValidityBadgeClass = (validity, displayStatus) => {
    if (displayStatus === 'accepted') {
      return 'bg-emerald-50 text-emerald-800 border-emerald-200';
    }
    if (displayStatus === 'lost') {
      return 'bg-stone-100 text-stone-700 border-stone-300';
    }
    if (validity.kind === 'expired' || displayStatus === 'expired') {
      return 'bg-amber-50 text-amber-900 border-amber-300';
    }
    if (validity.kind === 'open') {
      return 'bg-teal-50 text-teal-800 border-teal-200';
    }
    return 'bg-gray-50 text-gray-600 border-gray-200';
  };

  const formatStatusLabel = (status) => {
    const map = {
      draft: t('dashboard.quoteStatusDraft'),
      sent: t('dashboard.quoteStatusSent'),
      accepted: t('dashboard.quoteStatusAccepted'),
      expired: t('dashboard.quoteStatusExpired'),
      rejected: t('dashboard.quoteStatusRejected'),
      lost: t('dashboard.quoteStatusLost'),
    };
    return map[status] || status;
  };

  const formatValidityLabel = (validity, displayStatus) => {
    if (displayStatus === 'accepted') return t('dashboard.quoteStatusAccepted');
    if (displayStatus === 'lost') return t('dashboard.quoteStatusLost');
    if (validity.kind === 'expired' || displayStatus === 'expired') {
      return t('dashboard.validityExpired');
    }
    if (validity.kind === 'open') {
      if (validity.daysLeft === 0) return t('dashboard.validityOpen');
      if (validity.daysLeft === 1) return t('dashboard.validityDayLeft');
      return t('dashboard.validityDaysLeft', { count: validity.daysLeft });
    }
    return t('dashboard.validityNoDate');
  };

  const quoteStatusTabs = [
    { id: 'all', label: t('dashboard.quoteStatusAll'), count: quoteStatusCounts.all, icon: FileText },
    { id: 'draft', label: t('dashboard.quoteStatusDraft'), count: quoteStatusCounts.draft, icon: FileEdit },
    { id: 'sent', label: t('dashboard.quoteStatusSent'), count: quoteStatusCounts.sent, icon: Send },
    { id: 'accepted', label: t('dashboard.quoteStatusAccepted'), count: quoteStatusCounts.accepted, icon: CheckCircle2 },
    { id: 'expired', label: t('dashboard.quoteStatusExpired'), count: quoteStatusCounts.expired, icon: AlertTriangle },
    { id: 'lost', label: t('dashboard.quoteStatusLost'), count: quoteStatusCounts.lost, icon: Ban },
  ];

  const handleOpenConfigurator = () => {
    navigate('/configurator-select', { state: { preserveQuoteSession: true } });
  };

  /** Same as configurator "Nieuwe offerte": clear drawings, contact, and start fresh. */
  const handleCreateNewQuote = () => {
    if (!window.confirm(t('configurator.newOffersConfirm'))) return;
    startNewQuoteSession();
    clearActiveContactForConfigurator();
    navigate('/configurator-select', { state: { newQuote: true } });
  };

  const menuItems = [
    { id: 'overview', label: t('dashboard.menuOverview'), icon: LayoutDashboard },
    { id: 'contacts', label: t('dashboard.menuContacts'), icon: Users },
    { id: 'team', label: t('dashboard.menuTeam'), icon: UserPlus },
    { id: 'quotes', label: t('dashboard.menuQuotes'), icon: FileText },
    ...(isAdminUser(user)
      ? [{ id: 'pricing-settings', label: t('dashboard.menuPricingSettings'), icon: Settings }]
      : []),
  ];

  if (!isAuthenticated || !user) {
    if (authLoading) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-600">
          Loading…
        </div>
      );
    }
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-white border-r border-gray-200 transition-all duration-300 fixed h-full z-40`}>
        <div className="flex flex-col h-full">
          {/* Logo/Header */}
          <div className="h-16 border-b border-gray-200 flex items-center justify-between px-4">
            {sidebarOpen ? (
              <div className="flex items-center space-x-2">
                <img
                  src={APP_LOGO_SRC}
                  alt="config-go"
                  className="h-10 w-auto object-contain"
                />
              </div>
            ) : (
              <div className="mx-auto h-8 w-10 overflow-hidden">
                <img
                  src={APP_LOGO_SRC}
                  alt="config-go"
                  className="h-8 w-auto max-w-none object-left object-contain"
                />
              </div>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="h-8 w-8"
            >
              {sidebarOpen ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4">
            <div className="px-3 space-y-2">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => goToTab(item.id)}
                    className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all ${
                      isActive
                        ? 'bg-orange-50 text-orange-700 border border-orange-200'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <Icon className={`h-5 w-5 shrink-0 ${isActive ? 'text-orange-600' : 'text-gray-500'}`} />
                    {sidebarOpen && <span className="font-medium">{item.label}</span>}
                  </button>
                );
              })}
            </div>
          </nav>

          {/* User Section */}
          <div className="border-t border-gray-200 p-4">
            <div className={`flex items-center ${sidebarOpen ? 'justify-between' : 'justify-center'}`}>
              {sidebarOpen ? (
                <>
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                      <span className="text-orange-600 font-semibold text-sm">
                        {user.first_name[0]}{user.last_name[0]}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {user.first_name} {user.last_name}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{user.email}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleLogout}
                    className="h-8 w-8 text-gray-500 hover:text-red-600"
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleLogout}
                  className="h-8 w-8 text-gray-500 hover:text-red-600"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`flex-1 min-w-0 overflow-x-hidden transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-20'}`}>
        {/* Top Header */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{user.company_name}</h1>
                <p className="text-sm text-gray-600 mt-1">
                  Welcome back, {user.first_name} • {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                </p>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-end">
                <ConfiguratorNavActions
                  variant="light"
                  showReset={false}
                />
                <Button
                  onClick={handleOpenConfigurator}
                  className="bg-orange-600 hover:bg-orange-700 text-white shrink-0"
                >
                  Configurator
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="md:hidden"
                >
                  {mobileMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-b border-gray-200 px-4 py-2">
            <div className="flex flex-wrap gap-3">
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Button
                    key={item.id}
                    variant={activeTab === item.id ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      goToTab(item.id);
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-2.5 text-[0.95rem] leading-[1.25rem]"
                  >
                    <Icon className="h-4 w-4" />
                    <span>{item.label}</span>
                  </Button>
                );
              })}
            </div>
          </div>
        )}

        {/* Content Area */}
        <main className="p-6 min-w-0 overflow-x-hidden">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="border-l-4 border-l-blue-500 shadow-sm hover:shadow-md transition-shadow">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Total Contacts</CardTitle>
                    <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <Users className="h-5 w-5 text-blue-600" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-gray-900">{contactsTotal}</div>
                    <p className="text-xs text-gray-500 mt-1">Customer contacts in database</p>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-green-500 shadow-sm hover:shadow-md transition-shadow">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Team Members</CardTitle>
                    <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                      <UserPlus className="h-5 w-5 text-green-600" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-gray-900">{teamMembers.length}</div>
                    <p className="text-xs text-gray-500 mt-1">Active team members</p>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-orange-500 shadow-sm hover:shadow-md transition-shadow">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Total Quotes</CardTitle>
                    <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                      <FileText className="h-5 w-5 text-orange-600" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-gray-900">{quotes.length}</div>
                    <p className="text-xs text-gray-500 mt-1">All time quotes generated</p>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Activity */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="shadow-sm">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg font-semibold">Recent Quotes</CardTitle>
                        <CardDescription>Latest quotes created by your team</CardDescription>
                      </div>
                      <Activity className="h-5 w-5 text-gray-400" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    {quotesLoading ? (
                      <div className="text-center py-8 text-gray-500">Loading...</div>
                    ) : quotes.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                        <p>No quotes yet</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {quotes.slice(0, 5).map((quote) => (
                          <div
                            key={quote.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                              navigate(`/dashboard/quotes/${quote.id}`, {
                                state: {
                                  quote,
                                  returnPath: '/dashboard?tab=overview',
                                },
                              });
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                navigate(`/dashboard/quotes/${quote.id}`, {
                                  state: {
                                    quote,
                                    returnPath: '/dashboard?tab=overview',
                                  },
                                });
                              }
                            }}
                            className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-orange-300 transition-all cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
                          >
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-1">
                                <span className="font-semibold text-gray-900">{quote.quote_number}</span>
                                <Badge className={`${getStatusColor(quote.status)} text-xs`}>
                                  {quote.status}
                                </Badge>
                              </div>
                              <p className="text-xs text-gray-500">
                                {new Date(quote.created_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                })}
                              </p>
                            </div>
                            {quote.total_amount && (
                              <div className="text-right">
                                <div className="font-semibold text-gray-900">
                                  {new Intl.NumberFormat('nl-NL', {
                                    style: 'currency',
                                    currency: quote.currency || 'EUR',
                                  }).format(quote.total_amount)}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card className="shadow-sm">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg font-semibold">Quick Stats</CardTitle>
                        <CardDescription>Overview of your business</CardDescription>
                      </div>
                      <TrendingUp className="h-5 w-5 text-gray-400" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <Users className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">Active Contacts</p>
                            <p className="text-xs text-gray-500">This month</p>
                          </div>
                        </div>
                        <span className="text-lg font-bold text-gray-900">{contactsTotal}</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                            <FileText className="h-5 w-5 text-green-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">Quotes This Month</p>
                            <p className="text-xs text-gray-500">Generated</p>
                          </div>
                        </div>
                        <span className="text-lg font-bold text-gray-900">
                          {quotes.filter(q => {
                            const quoteDate = new Date(q.created_at);
                            const now = new Date();
                            return quoteDate.getMonth() === now.getMonth() && quoteDate.getFullYear() === now.getFullYear();
                          }).length}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Contacts Tab */}
          {activeTab === 'contacts' && (
            <div className="space-y-4 min-w-0 max-w-full overflow-x-hidden">
              <div className="min-w-0">
                <h2 className="text-2xl font-bold text-gray-900">Contacts</h2>
                <p className="text-sm text-gray-600 mt-1">Manage your customer contacts</p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {user && user.role === 'admin' && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={syncingGHL}
                        className="whitespace-nowrap"
                        onClick={async () => {
                          setSyncingGHL(true);
                          setGhlSyncMessage({ type: 'info', text: 'Full sync from GoHighLevel (all pages)…' });
                          try {
                            const response = await axios.post(
                              `${API_URL}/api/ghl/sync-contacts`,
                              { overwrite: false, incremental: false },
                              {
                                headers: {
                                  Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                                },
                              }
                            );
                            const d = response.data || {};
                            setGhlSyncMessage({
                              type: 'success',
                              text: `Full sync: ${d.synced ?? 0} new/updated, ${d.skipped ?? 0} skipped, ${d.total_ghl_contacts ?? 0} from GHL${d.errors ? `, ${d.errors} errors` : ''}.`,
                            });
                            await fetchContactsPage();
                            setTimeout(() => setGhlSyncMessage(null), 8000);
                          } catch (error) {
                            console.error('Error syncing GHL contacts:', error);
                            setGhlSyncMessage({
                              type: 'error',
                              text:
                                error.response?.data?.detail ||
                                'Failed to sync contacts from GHL. Please check your GHL API credentials.',
                            });
                            setTimeout(() => setGhlSyncMessage(null), 7000);
                          } finally {
                            setSyncingGHL(false);
                          }
                        }}
                      >
                        {syncingGHL ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Syncing...
                          </>
                        ) : (
                          <>
                            <ArrowRight className="mr-2 h-4 w-4" />
                            Sync from GHL
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={syncingGHL}
                        className="whitespace-nowrap text-gray-700"
                        title="Only contacts updated in GHL since the last sync"
                        onClick={async () => {
                          setSyncingGHL(true);
                          setGhlSyncMessage({ type: 'info', text: 'Incremental sync (updated since last sync)…' });
                          try {
                            const response = await axios.post(
                              `${API_URL}/api/ghl/sync-contacts`,
                              { overwrite: false, incremental: true },
                              {
                                headers: {
                                  Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                                },
                              }
                            );
                            const d = response.data || {};
                            setGhlSyncMessage({
                              type: 'success',
                              text: `${d.mode === 'incremental' ? 'Updates' : 'Full'} sync: ${d.synced ?? 0} synced (${d.updated ?? 0} updated), ${d.skipped ?? 0} skipped, ${d.total_ghl_contacts ?? 0} from GHL.`,
                            });
                            await fetchContactsPage();
                            setTimeout(() => setGhlSyncMessage(null), 8000);
                          } catch (error) {
                            console.error('Error syncing GHL contacts:', error);
                            setGhlSyncMessage({
                              type: 'error',
                              text:
                                error.response?.data?.detail ||
                                'Failed to sync contacts from GHL.',
                            });
                            setTimeout(() => setGhlSyncMessage(null), 7000);
                          } finally {
                            setSyncingGHL(false);
                          }
                        }}
                      >
                        Sync updates
                      </Button>
                    </>
                  )}
                  <Button size="sm" className="bg-orange-600 hover:bg-orange-700 shadow-sm whitespace-nowrap">
                    <Plus className="mr-2 h-4 w-4" />
                    Add Contact
                  </Button>
                </div>
                {ghlSyncMessage ? (
                  <p
                    className={`mt-2 text-sm ${
                      ghlSyncMessage.type === 'error'
                        ? 'text-red-600'
                        : ghlSyncMessage.type === 'success'
                          ? 'text-green-700'
                          : 'text-gray-600'
                    }`}
                  >
                    {ghlSyncMessage.text}
                  </p>
                ) : null}
              </div>

              <Card className="shadow-sm max-w-full overflow-hidden">
                <CardHeader className="pb-3">
                  <div className="relative max-w-full">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search contacts by name, email, phone, or city..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 max-w-full"
                    />
                  </div>
                </CardHeader>
                <CardContent className="overflow-x-hidden">
                  {contactsLoading ? (
                    <div className="text-center py-12 text-gray-500">Loading contacts...</div>
                  ) : contactsTotal === 0 ? (
                    <div className="text-center py-12">
                      <Users className="mx-auto h-16 w-16 text-gray-300 mb-4" />
                      <p className="text-gray-500 font-medium">No contacts found</p>
                      <p className="text-sm text-gray-400 mt-1">Get started by adding your first contact</p>
                    </div>
                  ) : (
                    <div className="w-full max-w-full overflow-x-hidden">
                      <table className="w-full table-fixed border-collapse text-sm">
                        <colgroup>
                          <col style={{ width: '44px' }} />
                          <col style={{ width: '16%' }} />
                          <col style={{ width: '13%' }} />
                          <col style={{ width: '20%' }} />
                          <col style={{ width: '14%' }} />
                          <col style={{ width: '14%' }} />
                          <col style={{ width: '18%' }} />
                        </colgroup>
                        <thead>
                          <tr className="border-b border-gray-200 text-left">
                            <th className="py-2 pr-2 font-semibold text-gray-700" aria-hidden />
                            <th className="py-2 px-2 font-semibold text-gray-700">Name</th>
                            <th className="py-2 px-2 font-semibold text-gray-700">Phone</th>
                            <th className="py-2 px-2 font-semibold text-gray-700">Email</th>
                            <th className="py-2 px-2 font-semibold text-gray-700">City</th>
                            <th className="py-2 px-2 font-semibold text-gray-700">Created</th>
                            <th className="py-2 pl-2 font-semibold text-gray-700">Tags</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedContacts.map((contact) => (
                            <tr
                              key={contact.id}
                              className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                              onClick={() => {
                                navigate(`/dashboard/contact/${contact.id}`);
                              }}
                            >
                              <td className="py-2.5 pr-2 align-middle">
                                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center shadow-sm">
                                  <span className="text-white font-semibold text-[10px]">
                                    {getInitials(contact.first_name, contact.last_name)}
                                  </span>
                                </div>
                              </td>
                              <td className="py-2.5 px-2 align-middle font-medium text-gray-900 overflow-hidden">
                                <span className="block truncate" title={`${contact.first_name || ''} ${contact.last_name || ''}`.trim()}>
                                  {contact.first_name} {contact.last_name}
                                </span>
                              </td>
                              <td className="py-2.5 px-2 align-middle text-gray-600 overflow-hidden">
                                <span className="block truncate" title={contact.phone || ''}>
                                  {contact.phone || '-'}
                                </span>
                              </td>
                              <td className="py-2.5 px-2 align-middle text-gray-600 overflow-hidden">
                                <span className="block truncate" title={contact.email || ''}>
                                  {contact.email || '-'}
                                </span>
                              </td>
                              <td className="py-2.5 px-2 align-middle text-gray-600 overflow-hidden">
                                <span className="block truncate" title={contact.city || ''}>
                                  {contact.city || '-'}
                                </span>
                              </td>
                              <td className="py-2.5 px-2 align-middle text-gray-600 text-xs overflow-hidden">
                                <span className="block truncate">
                                  {new Date(contact.created_at).toLocaleString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                  })}
                                </span>
                              </td>
                              <td className="py-2.5 pl-2 align-middle overflow-hidden">
                                {contact.notes ? (
                                  <span
                                    className="block truncate rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-xs text-gray-700"
                                    title={contact.notes}
                                  >
                                    {contact.notes}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      
                      {/* Pagination Controls - Only show if more than 50 contacts */}
                      {contactsTotal > contactsPerPage && (
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mt-6 pt-4 border-t border-gray-200">
                          <div className="text-sm text-gray-600 shrink-0">
                            Showing {contacts.length === 0 ? 0 : contactStartIndex + 1} to {Math.min(contactEndIndex, contactsTotal)} of {contactsTotal.toLocaleString()} contacts
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setContactCurrentPage((prev) => Math.max(1, prev - 1))}
                              disabled={contactCurrentPage === 1}
                            >
                              <ChevronLeft className="h-4 w-4" /> Previous
                            </Button>
                            <div className="flex flex-wrap items-center gap-1">
                              {getCompactPaginationPages(contactCurrentPage, totalContactPages).map((item, index) =>
                                item === 'ellipsis' ? (
                                  <span key={`ellipsis-${index}`} className="px-1.5 text-gray-400 select-none">
                                    …
                                  </span>
                                ) : (
                                  <Button
                                    key={item}
                                    variant={item === contactCurrentPage ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setContactCurrentPage(item)}
                                    className={
                                      item === contactCurrentPage
                                        ? 'bg-orange-600 hover:bg-orange-700 text-white min-w-9'
                                        : 'min-w-9'
                                    }
                                  >
                                    {item}
                                  </Button>
                                )
                              )}
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setContactCurrentPage((prev) => Math.min(totalContactPages, prev + 1))
                              }
                              disabled={contactCurrentPage === totalContactPages}
                            >
                              Next <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Team Tab */}
          {activeTab === 'team' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Team Members</h2>
                  <p className="text-sm text-gray-600 mt-1">Manage your team and permissions</p>
                </div>
                {user && (user.role === 'admin' || user.role === 'Admin') && (
                  <Button 
                    className="bg-orange-600 hover:bg-orange-700 shadow-sm"
                    onClick={() => {
                      console.log('Add Team Member clicked, opening dialog', { userRole: user.role, dialogOpen: teamMemberDialogOpen });
                      setTeamMemberDialogOpen(true);
                    }}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Team Member
                  </Button>
                )}
              </div>

              <Card className="shadow-sm">
                <CardContent className="pt-6">
                  {teamLoading ? (
                    <div className="text-center py-12 text-gray-500">Loading team members...</div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {teamMembers.map((member) => (
                        <Card key={member.id} className="hover:shadow-md transition-all border border-gray-200">
                          <CardHeader>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <div className="h-12 w-12 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center">
                                  <span className="text-white font-semibold text-sm">
                                    {member.first_name[0]}{member.last_name[0]}
                                  </span>
                                </div>
                                <div>
                                  <CardTitle className="text-base font-semibold">
                                    {member.first_name} {member.last_name}
                                  </CardTitle>
                                  <Badge variant="outline" className="mt-1 text-xs">
                                    {member.role}
                                  </Badge>
                                </div>
                              </div>
                              {user && (user.role === 'admin' || user.role === 'Admin') && user.id !== member.id && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openDeleteDialog(member)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  title="Delete team member"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </CardHeader>
                          <CardContent className="space-y-2">
                            <div className="flex items-center text-sm text-gray-600">
                              <Mail className="mr-2 h-4 w-4 text-gray-400" />
                              <span className="truncate">{member.email}</span>
                            </div>
                            {member.phone && (
                              <div className="flex items-center text-sm text-gray-600">
                                <Phone className="mr-2 h-4 w-4 text-gray-400" />
                                {member.phone}
                              </div>
                            )}
                            {member.last_login && (
                              <div className="flex items-center text-sm text-gray-600">
                                <Calendar className="mr-2 h-4 w-4 text-gray-400" />
                                Last login: {new Date(member.last_login).toLocaleDateString()}
                              </div>
                            )}
                            {isAdminUser(user) && (
                              <div className="pt-3 mt-2 border-t border-gray-100">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="w-full text-orange-700 border-orange-200 hover:bg-orange-50"
                                  onClick={() => viewMemberQuotes(member.id)}
                                >
                                  <FileText className="mr-2 h-4 w-4" />
                                  View quotes
                                  <span className="ml-1 text-gray-500">
                                    ({countQuotesForTeamMember(quotes, member, teamMembers)})
                                  </span>
                                </Button>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Pricing Settings Tab — admin only */}
          {activeTab === 'pricing-settings' && isAdminUser(user) && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Pricing Settings</h2>
                <p className="text-sm text-gray-600 mt-1">Configure silent markup and deduction percentages for quotes. Fixed EUR deductions are set per quote on the export screen.</p>
              </div>

              <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle>Margin Settings</CardTitle>
                  <CardDescription>
                    Silent percentage adjustments applied to every quote (not shown on the PDF). Use markup to increase prices and deduction to reduce them. Minimum markup is 35%.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="margin-enabled"
                      checked={pricingSettings.margin_enabled}
                      onChange={(e) => setPricingSettings({
                        ...pricingSettings,
                        margin_enabled: e.target.checked,
                        margin_percentage: e.target.checked && pricingSettings.margin_percentage < 35 ? 35 : pricingSettings.margin_percentage
                      })}
                      className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                    />
                    <Label htmlFor="margin-enabled" className="text-sm font-medium text-gray-700 cursor-pointer">
                      Enable Margin
                    </Label>
                  </div>
                  
                  {pricingSettings.margin_enabled && (
                    <div className="pl-7 space-y-2">
                      <Label htmlFor="margin-percentage" className="text-sm text-gray-600">
                        Margin Percentage (%) <span className="text-red-500">*Minimum 35%</span>
                      </Label>
                      <Input
                        id="margin-percentage"
                        type="number"
                        min="35"
                        max="100"
                        step="0.01"
                        value={pricingSettings.margin_percentage}
                        onChange={(e) => {
                          const inputValue = e.target.value;
                          // Allow empty input while typing
                          if (inputValue === '') {
                            setPricingSettings({
                              ...pricingSettings,
                              margin_percentage: ''
                            });
                            return;
                          }
                          const value = parseFloat(inputValue);
                          // Allow typing any number, validation happens on blur/save
                          if (!isNaN(value)) {
                            setPricingSettings({
                              ...pricingSettings,
                              margin_percentage: value
                            });
                          }
                        }}
                        onBlur={(e) => {
                          // Validate and correct on blur
                          const value = parseFloat(e.target.value);
                          if (isNaN(value) || value < 35) {
                            setPricingSettings({
                              ...pricingSettings,
                              margin_percentage: 35
                            });
                          }
                        }}
                        className="max-w-xs"
                      />
                      <p className="text-xs text-gray-500">
                        Enter the margin percentage (minimum 35%, e.g., 45 for 45% margin)
                      </p>
                    </div>
                  )}

                  <div className="border-t border-gray-200 pt-4 mt-2">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="deduction-margin-enabled"
                        checked={pricingSettings.deduction_margin_enabled}
                        onChange={(e) => setPricingSettings({
                          ...pricingSettings,
                          deduction_margin_enabled: e.target.checked,
                        })}
                        className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                      />
                      <Label htmlFor="deduction-margin-enabled" className="text-sm font-medium text-gray-700 cursor-pointer">
                        Enable deduction (%)
                      </Label>
                    </div>

                    {pricingSettings.deduction_margin_enabled && (
                      <div className="pl-7 space-y-2 mt-3">
                        <Label htmlFor="deduction-margin-percentage" className="text-sm text-gray-600">
                          Deduction percentage (%)
                        </Label>
                        <Input
                          id="deduction-margin-percentage"
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={pricingSettings.deduction_margin_percentage}
                          onChange={(e) => {
                            const inputValue = e.target.value;
                            if (inputValue === '') {
                              setPricingSettings({
                                ...pricingSettings,
                                deduction_margin_percentage: '',
                              });
                              return;
                            }
                            const value = parseFloat(inputValue);
                            if (!isNaN(value)) {
                              setPricingSettings({
                                ...pricingSettings,
                                deduction_margin_percentage: value,
                              });
                            }
                          }}
                          onBlur={(e) => {
                            const value = parseFloat(e.target.value);
                            if (isNaN(value) || value < 0) {
                              setPricingSettings({
                                ...pricingSettings,
                                deduction_margin_percentage: 0,
                              });
                            } else if (value > 100) {
                              setPricingSettings({
                                ...pricingSettings,
                                deduction_margin_percentage: 100,
                              });
                            }
                          }}
                          className="max-w-xs"
                        />
                        <p className="text-xs text-gray-500">
                          Silently reduces prices after markup and add-ons (0–100%, e.g. 10 for 10% off)
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle>Signed quote notifications</CardTitle>
                  <CardDescription>
                    When a client signs a quote, these addresses receive a notification email (in addition to the quote creator / &quot;Created by&quot; person). Defaults: jurgen@vmkkozijnen.nl and nick@vmkkozijnen.nl.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-2">
                    {(pricingSettings.signed_quote_notification_emails || []).map((email) => (
                      <li
                        key={email}
                        className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2"
                      >
                        <span className="text-sm text-gray-800 break-all">{email}</span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="shrink-0 text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() =>
                            setPricingSettings((prev) => ({
                              ...prev,
                              signed_quote_notification_emails: (
                                prev.signed_quote_notification_emails || []
                              ).filter((item) => item.toLowerCase() !== email.toLowerCase()),
                            }))
                          }
                        >
                          Remove
                        </Button>
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                    <div className="flex-1 space-y-1">
                      <Label htmlFor="signed-quote-notification-email" className="text-sm text-gray-600">
                        Add notification email
                      </Label>
                      <Input
                        id="signed-quote-notification-email"
                        type="email"
                        placeholder="name@vmkkozijnen.nl"
                        value={newNotificationEmail}
                        onChange={(e) => setNewNotificationEmail(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const next = newNotificationEmail.trim();
                            if (!next || !next.includes('@')) return;
                            setPricingSettings((prev) => {
                              const existing = prev.signed_quote_notification_emails || [];
                              if (existing.some((item) => item.toLowerCase() === next.toLowerCase())) {
                                return prev;
                              }
                              return {
                                ...prev,
                                signed_quote_notification_emails: [...existing, next],
                              };
                            });
                            setNewNotificationEmail('');
                          }
                        }}
                      />
                    </div>
                    <Button
                      type="button"
                      className="bg-orange-600 hover:bg-orange-700 shrink-0"
                      onClick={() => {
                        const next = newNotificationEmail.trim();
                        if (!next || !next.includes('@')) return;
                        setPricingSettings((prev) => {
                          const existing = prev.signed_quote_notification_emails || [];
                          if (existing.some((item) => item.toLowerCase() === next.toLowerCase())) {
                            return prev;
                          }
                          return {
                            ...prev,
                            signed_quote_notification_emails: [...existing, next],
                          };
                        });
                        setNewNotificationEmail('');
                      }}
                    >
                      Add email
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500">
                    The person listed as &quot;Created by&quot; on the quote is always notified automatically.
                  </p>
                </CardContent>
              </Card>

              <div className="flex justify-end">
                <Button
                  onClick={handleSavePricingSettings}
                  disabled={savingPricingSettings}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  {savingPricingSettings ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>
            </div>
          )}

          {/* Quotes Tab */}
          {activeTab === 'quotes' && (
            <div className="space-y-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{t('dashboard.quotesTitle')}</h2>
                  <p className="text-sm text-gray-600 mt-1">{t('dashboard.quotesSubtitle')}</p>
                </div>
                <Button className="bg-orange-600 hover:bg-orange-700 shadow-sm shrink-0" onClick={handleCreateNewQuote}>
                  <Plus className="mr-2 h-4 w-4" />
                  {t('dashboard.createQuote')}
                </Button>
              </div>

              {/* Status filters */}
              <div className="flex flex-wrap gap-2">
                {quoteStatusTabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = quoteStatusFilter === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => {
                        setQuoteStatusFilter(tab.id);
                        storeQuoteStatus(tab.id);
                        setQuoteCurrentPage(1);
                        clearStoredQuotePage();
                      }}
                      className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                        isActive
                          ? 'border-orange-300 bg-orange-50 text-orange-900 shadow-sm'
                          : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <Icon className={`h-3.5 w-3.5 ${isActive ? 'text-orange-600' : 'text-gray-400'}`} />
                      <span>{tab.label}</span>
                      <span
                        className={`rounded-md px-1.5 py-0.5 text-xs tabular-nums ${
                          isActive ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {tab.count}
                      </span>
                    </button>
                  );
                })}
              </div>

              <Card className="shadow-sm">
                <CardHeader className="pb-3">
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-col lg:flex-row lg:items-end gap-3">
                      <div className="flex-1">
                        <Label htmlFor="quote-search" className="text-xs text-gray-600 mb-1.5 block">
                          {t('dashboard.searchQuotes')}
                        </Label>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <Input
                            id="quote-search"
                            placeholder={t('dashboard.searchQuotesPlaceholder')}
                            value={quoteSearchTerm}
                            onChange={(e) => {
                              const value = e.target.value;
                              setQuoteSearchTerm(value);
                              storeQuoteSearch(value);
                            }}
                            className="pl-10"
                          />
                        </div>
                      </div>
                      {isAdminUser(user) && (
                        <div className="w-full lg:w-72 shrink-0">
                          <Label htmlFor="quote-creator-filter" className="text-xs text-gray-600 mb-1.5 block">
                            {t('dashboard.filterByTeamMember')}
                          </Label>
                          <Select
                            value={quoteCreatorFilter}
                            onValueChange={handleQuoteCreatorFilterChange}
                          >
                            <SelectTrigger id="quote-creator-filter" className="w-full">
                              <div className="flex items-center gap-2 truncate">
                                <Filter className="h-4 w-4 text-gray-500 shrink-0" />
                                <SelectValue placeholder={t('dashboard.allTeamMembers')} />
                              </div>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">{t('dashboard.allTeamMembers')}</SelectItem>
                              {quoteCreatorOptions.map((member) => (
                                <SelectItem key={member.id} value={String(member.id)}>
                                  {member.name}
                                  {member.quoteCount > 0 ? ` (${member.quoteCount})` : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  </div>
                  {isAdminUser(user) && quoteCreatorFilter !== 'all' && selectedCreatorLabel && (
                    <p className="text-xs text-gray-500 mt-2">
                      {t('dashboard.showingQuotesBy')}{' '}
                      <span className="font-medium text-gray-700">{selectedCreatorLabel}</span>
                    </p>
                  )}
                </CardHeader>
                <CardContent className="pt-2">
                  {quotesLoading ? (
                    <div className="text-center py-12 text-gray-500">{t('dashboard.loadingQuotes')}</div>
                  ) : filteredQuotes.length === 0 ? (
                    <div className="text-center py-12">
                      <FileText className="mx-auto h-16 w-16 text-gray-300 mb-4" />
                      <p className="text-gray-500 font-medium">
                        {quoteStatusFilter !== 'all'
                          ? t('dashboard.noQuotesForFilter')
                          : quoteCreatorFilter !== 'all'
                            ? t('dashboard.noQuotesForUser')
                            : quoteSearchTerm
                              ? t('dashboard.noQuotesFound')
                              : t('dashboard.noQuotesYet')}
                      </p>
                      <p className="text-sm text-gray-400 mt-1">
                        {quoteStatusFilter !== 'all'
                          ? t('dashboard.tryAnotherStatus')
                          : quoteCreatorFilter !== 'all'
                            ? t('dashboard.tryAnotherMember')
                            : quoteSearchTerm
                              ? t('dashboard.tryDifferentSearch')
                              : t('dashboard.createFirstQuote')}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-3">
                        {paginatedQuotes.map((quote) => {
                        const clientDetails = quote.quote_data?.clientDetails || {};
                        const clientName =
                          formatQuoteClientDisplayName(clientDetails) || clientDetails.name || '';
                        const clientEmail = clientDetails.email || '';
                        const creator =
                          teamMembers.length > 0 ? getQuoteCreatorInfo(quote, teamMembers) : null;
                        const displayStatus = getQuoteDisplayStatus(quote);
                        const validity = getValidityMeta(quote);
                        const locale = language === 'nl' ? 'nl-NL' : 'en-US';

                        return (
                          <div
                            key={quote.id}
                            className={`rounded-lg border border-gray-200 border-l-4 bg-white hover:shadow-md transition-all ${getStatusAccent(displayStatus)}`}
                          >
                            <div className="p-4">
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                  <div className="flex flex-wrap items-center gap-2 mb-2">
                                    <h3 className="text-base font-semibold text-gray-900 truncate">
                                      {quote.quote_number}
                                    </h3>
                                    <Badge
                                      variant="outline"
                                      className={`${getStatusColor(displayStatus)} text-xs font-medium capitalize`}
                                    >
                                      {formatStatusLabel(displayStatus)}
                                    </Badge>
                                    <Badge
                                      variant="outline"
                                      className={`${getValidityBadgeClass(validity, displayStatus)} text-xs font-medium inline-flex items-center gap-1`}
                                    >
                                      <Clock className="h-3 w-3" />
                                      {formatValidityLabel(validity, displayStatus)}
                                    </Badge>
                                    {typeof clientDetails.subjectToFinance === 'boolean' ? (
                                      <Badge
                                        variant="outline"
                                        className={`text-xs font-medium ${
                                          clientDetails.subjectToFinance
                                            ? 'bg-amber-50 text-amber-900 border-amber-200'
                                            : 'bg-slate-50 text-slate-700 border-slate-200'
                                        }`}
                                      >
                                        {t('dashboard.subjectToFinanceShort')}:{' '}
                                        {clientDetails.subjectToFinance
                                          ? t('common.yes')
                                          : t('common.no')}
                                      </Badge>
                                    ) : null}
                                  </div>

                                  <div className="flex flex-col sm:flex-row sm:flex-wrap gap-x-6 gap-y-1 text-sm">
                                    {clientName && (
                                      <p className="text-gray-900">
                                        <span className="text-gray-500">{t('dashboard.clientLabel')}: </span>
                                        <span className="font-medium">{clientName}</span>
                                        {clientEmail && (
                                          <span className="text-gray-500 ml-1.5 text-xs">
                                            <Mail className="inline mr-0.5 h-3 w-3" />
                                            {clientEmail}
                                          </span>
                                        )}
                                      </p>
                                    )}
                                    {isAdminUser(user) && creator && (
                                      <p className="text-xs text-gray-500">
                                        {t('dashboard.createdBy')}:{' '}
                                        <span className="text-gray-700">{creator.name}</span>
                                        {creator.email && (
                                          <span className="ml-1">({creator.email})</span>
                                        )}
                                      </p>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-400 mt-1.5">
                                    {t('dashboard.createdAt')}{' '}
                                    {new Date(quote.created_at).toLocaleString(locale, {
                                      month: 'long',
                                      day: 'numeric',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })}
                                  </p>
                                </div>
                                {(
                                  isAdminUser(user) || quoteBelongsToTeamMember(quote, user, teamMembers)
                                ) && (
                                  <div className="flex items-center gap-0.5 shrink-0">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => openDuplicateQuoteDialog(quote)}
                                      className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                                      title={t('dashboard.duplicateQuote')}
                                    >
                                      <Copy className="h-4 w-4" />
                                    </Button>
                                    {displayStatus !== 'lost' ? (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => openLostQuoteDialog(quote)}
                                        className="text-stone-600 hover:text-stone-800 hover:bg-stone-100"
                                        title={t('dashboard.markLost')}
                                      >
                                        <Ban className="h-4 w-4" />
                                      </Button>
                                    ) : null}
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => openDeleteQuoteDialog(quote)}
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                      title={t('dashboard.deleteQuote')}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                )}
                              </div>

                              <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-3">
                                <div className="flex flex-1 flex-wrap gap-3">
                                  {quote.total_amount != null && (
                                    <div className="rounded-md bg-gray-50 px-3 py-2 min-w-[140px]">
                                      <p className="text-[11px] uppercase tracking-wide text-gray-500">
                                        {t('dashboard.totalAmount')}
                                      </p>
                                      <p className="text-lg font-semibold text-gray-900 tabular-nums">
                                        {new Intl.NumberFormat('nl-NL', {
                                          style: 'currency',
                                          currency: quote.currency || 'EUR',
                                        }).format(quote.total_amount)}
                                      </p>
                                    </div>
                                  )}
                                  {quote.valid_until && (
                                    <div
                                      className={`rounded-md px-3 py-2 min-w-[140px] ${
                                        validity.isExpired && displayStatus !== 'accepted'
                                          ? 'bg-amber-50'
                                          : 'bg-teal-50/60'
                                      }`}
                                    >
                                      <p className="text-[11px] uppercase tracking-wide text-gray-500">
                                        {t('dashboard.validUntil')}
                                      </p>
                                      <p
                                        className={`text-sm font-semibold tabular-nums ${
                                          validity.isExpired && displayStatus !== 'accepted'
                                            ? 'text-amber-900'
                                            : 'text-teal-900'
                                        }`}
                                      >
                                        {new Date(quote.valid_until).toLocaleDateString(locale, {
                                          month: 'long',
                                          day: 'numeric',
                                          year: 'numeric',
                                        })}
                                      </p>
                                    </div>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => openNotesDialog(quote)}
                                    title={
                                      quote.quote_data?.staffNotes
                                        ? String(quote.quote_data.staffNotes)
                                        : t('dashboard.notes')
                                    }
                                    className={`group relative flex h-[58px] w-[120px] max-w-[120px] shrink-0 flex-col items-stretch overflow-hidden rounded-md border text-left shadow-sm transition-colors ${
                                      quote.quote_data?.staffNotes
                                        ? 'border-amber-300/80 bg-[#FFF8E7] hover:bg-[#FFF3D6]'
                                        : 'border-dashed border-gray-200 bg-gray-50 hover:bg-gray-100'
                                    }`}
                                  >
                                    {/* folded corner */}
                                    <span
                                      aria-hidden
                                      className={`pointer-events-none absolute right-0 top-0 h-0 w-0 border-l-[14px] border-t-[14px] border-l-transparent ${
                                        quote.quote_data?.staffNotes
                                          ? 'border-t-amber-200/90'
                                          : 'border-t-gray-200'
                                      }`}
                                    />
                                    <span className="flex min-w-0 items-center gap-1.5 px-2.5 pt-2 pr-4">
                                      <StickyNote
                                        className={`h-3.5 w-3.5 shrink-0 ${
                                          quote.quote_data?.staffNotes
                                            ? 'text-amber-700'
                                            : 'text-gray-400'
                                        }`}
                                      />
                                      <span
                                        className={`min-w-0 truncate text-[10px] font-semibold uppercase tracking-wide ${
                                          quote.quote_data?.staffNotes
                                            ? 'text-amber-800/80'
                                            : 'text-gray-400'
                                        }`}
                                      >
                                        {t('dashboard.notes')}
                                      </span>
                                      {quote.quote_data?.staffNotes ? (
                                        <span className="ml-auto h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                                      ) : null}
                                    </span>
                                    <span
                                      className={`mt-auto min-w-0 truncate px-2.5 pb-2 text-xs leading-tight ${
                                        quote.quote_data?.staffNotes
                                          ? 'font-medium text-amber-950'
                                          : 'font-normal text-gray-400'
                                      }`}
                                    >
                                      {quote.quote_data?.staffNotes
                                        ? String(quote.quote_data.staffNotes)
                                        : t('dashboard.notesEmpty')}
                                    </span>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => openOpdrachtbonDialog(quote)}
                                    title={t('dashboard.opdrachtbon')}
                                    className={`flex h-[58px] w-[58px] shrink-0 flex-col items-center justify-center rounded-md border shadow-sm transition-colors ${
                                      quote.quote_data?.opdrachtbon
                                        ? 'border-purple-300 bg-purple-50 text-[#5B2C8A] hover:bg-purple-100'
                                        : 'border-dashed border-gray-200 bg-gray-50 text-gray-400 hover:bg-gray-100 hover:text-[#5B2C8A]'
                                    }`}
                                  >
                                    <ClipboardList className="h-5 w-5" />
                                    <span className="mt-0.5 text-[9px] font-semibold uppercase tracking-wide">
                                      {t('dashboard.opdrachtbonShort')}
                                    </span>
                                  </button>
                                </div>
                                <div className="flex items-center gap-2 sm:shrink-0 flex-wrap justify-end">
                                  {clientDetails.phone ? (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      asChild
                                      title={clientDetails.phone}
                                      className="px-2"
                                    >
                                      <a href={`tel:${String(clientDetails.phone).replace(/\s+/g, '')}`}>
                                        <Phone className="h-4 w-4" />
                                        <span className="sr-only">{clientDetails.phone}</span>
                                      </a>
                                    </Button>
                                  ) : null}
                                  {clientDetails.email ? (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      asChild
                                      title={clientDetails.email}
                                      className="px-2"
                                    >
                                      <a href={`mailto:${clientDetails.email}`}>
                                        <Mail className="h-4 w-4" />
                                        <span className="sr-only">{clientDetails.email}</span>
                                      </a>
                                    </Button>
                                  ) : null}
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      const quotesPage = getCurrentQuotesPage();
                                      navigate(`/dashboard/quotes/${quote.id}`, {
                                        state: {
                                          quote,
                                          returnPath: buildQuotesDashboardReturnPath(
                                            location.search,
                                            quotesPage,
                                            {
                                              q: quoteSearchTerm,
                                              status: quoteStatusFilter,
                                            },
                                          ),
                                          quotesPage,
                                          quoteSearch: quoteSearchTerm,
                                          quoteStatus: quoteStatusFilter,
                                        },
                                      });
                                    }}
                                  >
                                    <Eye className="mr-2 h-4 w-4" />
                                    {t('dashboard.viewQuoteButton')}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      </div>
                      
                      {/* Pagination Controls - Only show if more than 5 quotes */}
                      {filteredQuotes.length > quotesPerPage && (
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-6 pt-4 border-t border-gray-200">
                          <div className="text-sm text-gray-600">
                            {t('dashboard.showingQuotesRange', {
                              from: startIndex + 1,
                              to: Math.min(endIndex, filteredQuotes.length),
                              total: filteredQuotes.length,
                            })}
                          </div>
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => updateQuotePage(quoteCurrentPage - 1)}
                              disabled={quoteCurrentPage === 1}
                              className="flex items-center"
                            >
                              <ChevronLeft className="h-4 w-4 mr-1" />
                              {t('dashboard.previous')}
                            </Button>
                            
                            <div className="flex flex-wrap items-center gap-1">
                              {getCompactPaginationPages(quoteCurrentPage, totalQuotePages).map((item, index) =>
                                item === 'ellipsis' ? (
                                  <span key={`q-ellipsis-${index}`} className="px-1.5 text-gray-400 select-none">
                                    …
                                  </span>
                                ) : (
                                  <Button
                                    key={item}
                                    variant={item === quoteCurrentPage ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => updateQuotePage(item)}
                                    className={
                                      item === quoteCurrentPage
                                        ? 'bg-orange-600 hover:bg-orange-700 text-white min-w-9'
                                        : 'min-w-9'
                                    }
                                  >
                                    {item}
                                  </Button>
                                )
                              )}
                            </div>
                            
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => updateQuotePage(quoteCurrentPage + 1)}
                              disabled={quoteCurrentPage === totalQuotePages}
                              className="flex items-center"
                            >
                              {t('dashboard.next')}
                              <ChevronRight className="h-4 w-4 ml-1" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </main>
      </div>

      {/* Quote View Dialog */}
      <QuoteView 
        quote={selectedQuote} 
        open={quoteViewOpen} 
        onOpenChange={setQuoteViewOpen}
      />

      {/* Contact Details Dialog */}
      <Dialog open={contactDetailsOpen} onOpenChange={setContactDetailsOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">Contact Details</DialogTitle>
            <DialogDescription>
              Complete information for {selectedContact?.first_name} {selectedContact?.last_name}
            </DialogDescription>
          </DialogHeader>
          {selectedContact && (
            <div className="space-y-6 mt-4">
              {/* Header with Avatar */}
              <div className="flex items-center space-x-4 pb-4 border-b">
                <div className="h-16 w-16 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center shadow-md">
                  <span className="text-white font-semibold text-lg">
                    {getInitials(selectedContact.first_name, selectedContact.last_name)}
                  </span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {selectedContact.first_name} {selectedContact.last_name}
                  </h3>
                  {selectedContact.notes && (
                    <Badge variant="outline" className="mt-1">
                      {selectedContact.notes}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Contact Information Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Email */}
                {selectedContact.email && (
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Email</label>
                    <div className="flex items-center space-x-2">
                      <Mail className="h-4 w-4 text-gray-400" />
                      <p className="text-sm text-gray-900">{selectedContact.email}</p>
                    </div>
                  </div>
                )}

                {/* Phone */}
                {selectedContact.phone && (
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Phone</label>
                    <div className="flex items-center space-x-2">
                      <Phone className="h-4 w-4 text-gray-400" />
                      <p className="text-sm text-gray-900">{selectedContact.phone}</p>
                    </div>
                  </div>
                )}

                {/* Address */}
                {(selectedContact.address || selectedContact.city || selectedContact.postal_code) && (
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Address</label>
                    <div className="flex items-start space-x-2">
                      <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                      <p className="text-sm text-gray-900">
                        {selectedContact.address && selectedContact.address}
                        {selectedContact.city && (selectedContact.address ? `, ${selectedContact.city}` : selectedContact.city)}
                        {selectedContact.postal_code && ` ${selectedContact.postal_code}`}
                        {selectedContact.country && `, ${selectedContact.country}`}
                      </p>
                    </div>
                  </div>
                )}

                {/* Created Date */}
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Created</label>
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <p className="text-sm text-gray-900">
                      {new Date(selectedContact.created_at).toLocaleString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                </div>

                {/* Notes/Tags */}
                {selectedContact.notes && (
                  <div className="space-y-1 md:col-span-2">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Tags / Notes</label>
                    <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md border border-gray-200">
                      {selectedContact.notes}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Team Member Dialog */}
      <Dialog open={teamMemberDialogOpen} onOpenChange={setTeamMemberDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
            <DialogDescription>
              Create a new user account. Admin can see all quotes, users can only see their own.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  value={newTeamMember.first_name}
                  onChange={(e) => setNewTeamMember({...newTeamMember, first_name: e.target.value})}
                  placeholder="John"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  value={newTeamMember.last_name}
                  onChange={(e) => setNewTeamMember({...newTeamMember, last_name: e.target.value})}
                  placeholder="Doe"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={newTeamMember.email}
                onChange={(e) => setNewTeamMember({...newTeamMember, email: e.target.value})}
                placeholder="user@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password *</Label>
              <Input
                id="password"
                type="password"
                value={newTeamMember.password}
                onChange={(e) => setNewTeamMember({...newTeamMember, password: e.target.value})}
                placeholder="Enter password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role *</Label>
              <Select
                value={newTeamMember.role}
                onValueChange={(value) => setNewTeamMember({...newTeamMember, role: value})}
              >
                <SelectTrigger id="role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin (Can see all quotes)</SelectItem>
                  <SelectItem value="user">User (Can only see own quotes)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone (Optional)</Label>
              <Input
                id="phone"
                value={newTeamMember.phone}
                onChange={(e) => setNewTeamMember({...newTeamMember, phone: e.target.value})}
                placeholder="+31 6 12345678"
              />
            </div>
            <div className="flex justify-end space-x-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setTeamMemberDialogOpen(false);
                  setNewTeamMember({
                    email: '',
                    password: '',
                    first_name: '',
                    last_name: '',
                    role: 'user',
                    phone: ''
                  });
                }}
              >
                Cancel
              </Button>
              <Button
                className="bg-orange-600 hover:bg-orange-700"
                onClick={async () => {
                  if (!newTeamMember.email || !newTeamMember.password || !newTeamMember.first_name || !newTeamMember.last_name) {
                    alert('Please fill in all required fields');
                    return;
                  }
                  
                  try {
                    setCreatingMember(true);
                    await axios.post(
                      `${API_URL}/api/team`,
                      {
                        ...newTeamMember,
                        company_id: user.company_id
                      },
                      {
                        headers: {
                          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                        }
                      }
                    );
                    
                    // Refresh team members list
                    const teamRes = await axios.get(`${API_URL}/api/team`, {
                      headers: {
                        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                      }
                    });
                    setTeamMembers(teamRes.data);
                    
                    // Reset form and close dialog
                    setNewTeamMember({
                      email: '',
                      password: '',
                      first_name: '',
                      last_name: '',
                      role: 'user',
                      phone: ''
                    });
                    setTeamMemberDialogOpen(false);
                    alert('Team member added successfully!');
                  } catch (error) {
                    console.error('Error creating team member:', error);
                    alert(error.response?.data?.detail || 'Failed to create team member. Please try again.');
                  } finally {
                    setCreatingMember(false);
                  }
                }}
                disabled={creatingMember}
              >
                {creatingMember ? 'Creating...' : 'Create Member'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Team Member Confirmation Dialog */}
      <Dialog open={deleteMemberDialogOpen} onOpenChange={setDeleteMemberDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Team Member</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {memberToDelete ? `${memberToDelete.first_name} ${memberToDelete.last_name}` : 'this team member'}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end space-x-3 mt-6">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteMemberDialogOpen(false);
                setMemberToDelete(null);
              }}
              disabled={deletingMember}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteMember}
              disabled={deletingMember}
            >
              {deletingMember ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Duplicate Quote Confirmation Dialog */}
      <Dialog
        open={duplicateQuoteDialogOpen}
        onOpenChange={(open) => {
          if (duplicatingQuote) return;
          setDuplicateQuoteDialogOpen(open);
          if (!open) setQuoteToDuplicate(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('dashboard.duplicateQuoteTitle')}</DialogTitle>
            <DialogDescription>
              {t('dashboard.duplicateQuoteDescription', {
                number: quoteToDuplicate?.quote_number || '',
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end space-x-3 mt-6">
            <Button
              variant="outline"
              onClick={() => {
                setDuplicateQuoteDialogOpen(false);
                setQuoteToDuplicate(null);
              }}
              disabled={duplicatingQuote}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleDuplicateQuote}
              disabled={duplicatingQuote}
              className="bg-slate-900 hover:bg-slate-800 text-white"
            >
              {duplicatingQuote ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('dashboard.duplicateQuoteDuplicating')}
                </>
              ) : (
                <>
                  <Copy className="mr-2 h-4 w-4" />
                  {t('dashboard.duplicateQuoteConfirm')}
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Quote Confirmation Dialog */}
      <Dialog open={deleteQuoteDialogOpen} onOpenChange={setDeleteQuoteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Quote</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete{' '}
              {quoteToDelete?.quote_number ? `quote ${quoteToDelete.quote_number}` : 'this quote'}?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end space-x-3 mt-6">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteQuoteDialogOpen(false);
                setQuoteToDelete(null);
              }}
              disabled={deletingQuote}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteQuote}
              disabled={deletingQuote}
            >
              {deletingQuote ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mark quote as lost */}
      <Dialog
        open={lostQuoteDialogOpen}
        onOpenChange={(open) => {
          setLostQuoteDialogOpen(open);
          if (!open) setQuoteToMarkLost(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ban className="h-5 w-5 text-stone-600" />
              {t('dashboard.markLostTitle')}
            </DialogTitle>
            <DialogDescription>
              {quoteToMarkLost?.quote_number
                ? `${quoteToMarkLost.quote_number} — ${t('dashboard.markLostDescription')}`
                : t('dashboard.markLostDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end space-x-3 mt-6">
            <Button
              variant="outline"
              onClick={() => {
                setLostQuoteDialogOpen(false);
                setQuoteToMarkLost(null);
              }}
              disabled={markingQuoteLost}
            >
              {t('dashboard.notesClose')}
            </Button>
            <Button
              onClick={handleMarkQuoteLost}
              disabled={markingQuoteLost}
              className="bg-stone-700 hover:bg-stone-800 text-white"
            >
              {markingQuoteLost ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('dashboard.markLostMarking')}
                </>
              ) : (
                <>
                  <Ban className="mr-2 h-4 w-4" />
                  {t('dashboard.markLostConfirm')}
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quote notes / reminders */}
      <Dialog
        open={notesDialogOpen}
        onOpenChange={(open) => {
          setNotesDialogOpen(open);
          if (!open) {
            setNotesQuote(null);
            setNotesError('');
            setNotesSavedFlash(false);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StickyNote className="h-5 w-5 text-amber-600" />
              {t('dashboard.notesTitle')}
            </DialogTitle>
            <DialogDescription>
              {notesQuote?.quote_number
                ? `${notesQuote.quote_number} — ${t('dashboard.notesDescription')}`
                : t('dashboard.notesDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 space-y-3">
            <Textarea
              value={notesText}
              onChange={(e) => {
                setNotesText(e.target.value);
                setNotesSavedFlash(false);
              }}
              placeholder={t('dashboard.notesPlaceholder')}
              rows={6}
              className="resize-y min-h-[140px]"
            />
            {notesError ? (
              <p className="text-sm text-red-600">{notesError}</p>
            ) : null}
            {notesSavedFlash ? (
              <p className="text-sm text-emerald-600">{t('dashboard.notesSaved')}</p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setNotesDialogOpen(false)}
                disabled={savingNotes}
              >
                {t('dashboard.notesClose')}
              </Button>
              <Button
                onClick={handleSaveQuoteNotes}
                disabled={savingNotes}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                {savingNotes ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('dashboard.notesSaving')}
                  </>
                ) : (
                  t('dashboard.notesSave')
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Opdrachtbon / checklist (VMK PDF format) */}
      <Dialog
        open={opdrachtbonDialogOpen}
        onOpenChange={(open) => {
          setOpdrachtbonDialogOpen(open);
          if (!open) {
            setOpdrachtbonQuote(null);
            setOpdrachtbonData(null);
            setOpdrachtbonError('');
            setOpdrachtbonSavedFlash(false);
          }
        }}
      >
        <DialogContent className="flex max-h-[92vh] max-w-5xl flex-col gap-0 overflow-hidden p-0 sm:rounded-lg">
          <DialogHeader className="shrink-0 border-b px-6 py-4">
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-[#5B2C8A]" />
              {t('dashboard.opdrachtbonTitle')}
            </DialogTitle>
            <DialogDescription>
              {opdrachtbonQuote?.quote_number
                ? `${opdrachtbonQuote.quote_number} — ${t('dashboard.opdrachtbonDescription')}`
                : t('dashboard.opdrachtbonDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
            {opdrachtbonData ? (
              <OpdrachtbonEditor
                value={opdrachtbonData}
                onChange={(next) => {
                  setOpdrachtbonData(next);
                  setOpdrachtbonSavedFlash(false);
                }}
                quoteNumber={opdrachtbonQuote?.quote_number}
              />
            ) : null}
          </div>
          <div className="flex shrink-0 items-center justify-between gap-3 border-t bg-gray-50 px-6 py-3">
            <div className="min-w-0 text-sm">
              {opdrachtbonError ? (
                <p className="text-red-600">{opdrachtbonError}</p>
              ) : null}
              {opdrachtbonSavedFlash ? (
                <p className="text-emerald-600">{t('dashboard.opdrachtbonSaved')}</p>
              ) : null}
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpdrachtbonDialogOpen(false)}
                disabled={savingOpdrachtbon}
              >
                {t('dashboard.notesClose')}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handlePrintOpdrachtbon}
                disabled={savingOpdrachtbon || !opdrachtbonData}
                className="border-[#5B2C8A] text-[#5B2C8A] hover:bg-purple-50"
              >
                <Printer className="mr-2 h-4 w-4" />
                {t('dashboard.opdrachtbonPrint')}
              </Button>
              <Button
                type="button"
                onClick={handleSaveOpdrachtbon}
                disabled={savingOpdrachtbon}
                className="bg-[#5B2C8A] hover:bg-[#4a2270] text-white"
              >
                {savingOpdrachtbon ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('dashboard.opdrachtbonSaving')}
                  </>
                ) : (
                  t('dashboard.opdrachtbonSave')
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
